import { LCLocaleProvider } from "../../../frameWork";
import React, { useEffect, useRef, useState } from "react";
import { Button, message, Modal, Spin } from "antd";
import styles from "./CustomNavigation.module.scss";
import { CustomTable } from "../../../components/CustomDraggableTable";
import GetNavigationItemsAction from "../action/GetNavigationItemsAction";
import { urlName } from "../../../config";
import GetNavigationItemsStore from "../store/GetNavigationItemsStore";
import UpdateNavigationListAction from "../action/UpdateNavigationListAction";
import UpdateNavigationListStore from "../store/UpdateNavigationListStore";
import { ModalForm } from "../../../components/ModalForm";
import { NavModalFields } from "./NavigationModalFields";
import UpdateImageAssetsAction from "../../IntegrationCustomization/action/UpdateImageAssetsAction";
import UpdateImageAssetsStore from "../../IntegrationCustomization/store/UpdateImageAssetsStore";
import CreateNavigationAction from "../action/CreateNavigationAction";
import CreateNavigationStore from "../store/CreateNavigationStore";
import DeleteNavigationItemStore from "../store/DeleteNavigationItemStore";
import DeleteNavigationItemAction from "../action/DeleteNavigationItemAction";
import { validateNavData } from "../helpers/APIValidatorNewNav";

const NAME_LIMIT = 25;
const TOOLTIP_LIMIT = 100;
const INITIAL_NAV_DATA = {
	name: "",
	tooltip: "",
	type: "EXTERNAL",
	link: "",
	visibility: true,
	icon: "",
	iconType: "SVG",
	domainName: urlName,
	position: "TOP",
	background: "transparent",
	padding: 0,
};

const SECTIONS = [
	{ id: "top", label: "Right Top", showHeader: true },
	{ id: "middle", label: "Right Middle", showHeader: false },
	{ id: "bottom", label: "Right Bottom", showHeader: false },
	{ id: "left", label: "Left Bottom", showHeader: false },
];

const getProfileId = () => localStorage.getItem("profileId");

export const COLUMNS = [
	{ key: "move", label: "Move", width: "0.5fr" },
	{ key: "name", label: "Name", width: "1fr" },
	{ key: "icon", label: "Icon", width: "0.5fr" },
	{ key: "tooltip", label: "Tooltip", width: "1fr" },
	{ key: "link", label: "Link", width: "2.5fr" },
	{ key: "tabType", label: "Type", width: "1fr" },
	{ key: "type", label: "Action", width: "1fr" },
	{ key: "visibility", label: "Visibility", width: "0.5fr" },
	{ key: "action", label: "", width: "0.5fr" },
];
export const CustomNavigationbarWrapper = ({ profile }) => {
	const [topSection, setTopSection] = useState([]);
	const [middleSection, setMiddleSection] = useState([]);
	const [bottomSection, setBottomSection] = useState([]);
	const [leftSection, setLeftSection] = useState([]);
	const [loader, setLoader] = useState(false);
	const [dragInfo, setDragInfo] = useState(null);
	const [updateLoader, setUpdateLoader] = useState(false);
	const [addModal, setAddModal] = useState(false);
	const savedNavStylesRef = useRef({}); // tabId → { padding, background }
	const [createNavData, setCreateNavData] = useState({
		name: "",
		tooltip: "",
		type: "EXTERNAL",
		link: "",
		visibility: true,
		icon: "",
		iconType: "SVG",
		domainName: urlName,
		position: "TOP",
		background: "transparent",
		padding: 0,
	});

	useEffect(() => {
		const SCROLL_ZONE = 80; // px from top/bottom
		const SCROLL_SPEED = 20;

		const handleDragOver = (e) => {
			if (!dragInfo) return;

			const { clientY } = e;
			const viewportHeight = window.innerHeight;

			// Scroll up
			if (clientY < SCROLL_ZONE) {
				window.scrollBy({ top: -SCROLL_SPEED, behavior: "auto" });
			}

			// Scroll down
			if (clientY > viewportHeight - SCROLL_ZONE) {
				window.scrollBy({ top: SCROLL_SPEED, behavior: "auto" });
			}
		};

		window.addEventListener("dragover", handleDragOver);

		return () => {
			window.removeEventListener("dragover", handleDragOver);
		};
	}, [dragInfo]);

	useEffect(() => {
		setLoader(true);
		GetNavigationItemsAction.getNavigation(urlName, profile ? getProfileId() : null);

		const responseHandler = (store) => {
			let response = store.getResponse();
			if (response.status == 200) {
				if (response.message) message.success(response.message || "Navigation updated successfully");
				setLoader(true);
				GetNavigationItemsAction.getNavigation(urlName, profile ? getProfileId() : null);
				setAddModal(false);
			} else {
				message.error((response.data && response.data.message) || "Navigation updated failed");
			}
			setUpdateLoader(false);
		};

		const mergeStyles = (items) =>
			(items || []).map((item) => {
				const saved = savedNavStylesRef.current[item.tabId];
				if (!saved) return item;
				return {
					...item,
					padding: item.padding !== undefined ? item.padding : saved.padding,
					background: item.background || saved.background,
				};
			});

		const getNavDetails = () => {
			let response = GetNavigationItemsStore.getResponse();
			if (response.status == 200) {
				setTopSection(mergeStyles(response.navigations.top));
				setMiddleSection(mergeStyles(response.navigations.middle));
				setBottomSection(mergeStyles(response.navigations.bottom));
				setLeftSection(mergeStyles(response.navigations.left));
			} else {
				message.error((response.data && response.data.message) || "Failed to fetch navigation");
			}
			setLoader(false);
		};

		const updateImageAssesResponse = () => {
			let response = UpdateImageAssetsStore.getResponse();
			if (response.status == 200) {
				message.success("Image uploaded successfully");
				setCreateNavData((prev) => ({
					...prev,
					icon: response.imageLink,
					imageId: response.imageId,
				}));
			} else {
				message.error((response.data && response.data.message) || "Image upload failed");
			}
		};

		const deleteHandler = () => responseHandler(DeleteNavigationItemStore);
		const createHandler = () => responseHandler(CreateNavigationStore);
		const updateHandler = () => responseHandler(UpdateNavigationListStore);

		GetNavigationItemsStore.bind(getNavDetails);
		UpdateNavigationListStore.bind(updateHandler);
		UpdateImageAssetsStore.bind(updateImageAssesResponse);
		CreateNavigationStore.bind(createHandler);
		DeleteNavigationItemStore.bind(deleteHandler);

		return () => {
			GetNavigationItemsStore.unbind(getNavDetails);
			UpdateNavigationListStore.unbind(updateHandler);
			UpdateImageAssetsStore.unbind(updateImageAssesResponse);
			CreateNavigationStore.unbind(createHandler);
			DeleteNavigationItemStore.unbind(deleteHandler);
		};
	}, []);
	const getData = (tableId) => {
		switch (tableId) {
			case "top":
				return [...topSection];
			case "middle":
				return [...middleSection];
			case "bottom":
				return [...bottomSection];
			case "left":
				return [...leftSection];
			default:
				return [];
		}
	};

	const setData = (tableId, newData) => {
		switch (tableId) {
			case "top":
				setTopSection(newData);
				break;
			case "middle":
				setMiddleSection(newData);
				break;
			case "bottom":
				setBottomSection(newData);
				break;
			case "left":
				setLeftSection(newData);
			default:
				break;
		}
	};
	const onDragStart = (tableId, index) => {
		setDragInfo({ tableId, index });
	};
	const onReorder = (targetTable, targetIndex) => {
		if (!dragInfo) return;

		if (dragInfo.tableId !== targetTable) return;

		const data = getData(targetTable);
		const [moved] = data.splice(dragInfo.index, 1);
		data.splice(targetIndex, 0, moved);

		setData(targetTable, [...data]);

		setDragInfo({ tableId: targetTable, index: targetIndex });
	};

	const onDrop = (targetTable) => {
		if (!dragInfo) return;

		const sourceData = getData(dragInfo.tableId);
		const targetData = getData(targetTable);

		const movedItem = sourceData[dragInfo.index];

		if (
			targetTable === "left" &&
			movedItem.type !== "INTERNAL" &&
			movedItem.type !== "EXTERNAL"
		) {
			message.error(
				"Only Main Content or External items can be moved to Left Navigation",
			);
			setDragInfo(null);
			return;
		}

		if (dragInfo.tableId === targetTable) return;

		const [moved] = sourceData.splice(dragInfo.index, 1);
		targetData.push(moved);

		setData(dragInfo.tableId, [...sourceData]);
		setData(targetTable, [...targetData]);

		setDragInfo(null);
	};

	const handleVisibilityChange = (row, visibility) => {
		const sets = {
			top: [topSection, setTopSection],
			middle: [middleSection, setMiddleSection],
			bottom: [bottomSection, setBottomSection],
			left: [leftSection, setLeftSection],
		};

		for (const [data, setData] of Object.values(sets)) {
			const index = data.findIndex((r) => r.name === row.name);
			if (index !== -1) {
				const newData = [...data];
				newData[index] = { ...newData[index], visibility };
				setData(newData);
				return;
			}
		}
	};

	const saveNavData = () => {
		let data = {
			navigations: {
				top: topSection,
				middle: middleSection,
				bottom: bottomSection,
				left: leftSection,
			},
		};
		if (profile) {
			data['profileId'] = getProfileId();
		}
		setUpdateLoader(true);
		UpdateNavigationListAction.updateNavigation(data);
	};

	const onClickAddModal = () => {
		setAddModal(!addModal);
		setCreateNavData(INITIAL_NAV_DATA);
	};
	const changeNavItems = (field, value) => {
		if (field === "name" && value.length > NAME_LIMIT) {
			message.error(`Maximum character limit reached (${NAME_LIMIT} Characters)`);
			return;
		}

		if (field === "tooltip" && value.length > TOOLTIP_LIMIT) {
			message.error(`Maximum character limit reached (${TOOLTIP_LIMIT} Characters)`);
			return;
		}

		setCreateNavData((prev) => {
			let updated = { ...prev };

			// SVG icon handling
			if (field === "iconType" && (value === "SVG" || value === "CUSTOM_SVG")) {
				updated.iconType = value;
				updated.icon = "";
				delete updated.imageId;
				return updated;
			}

			// Generic field update
			updated[field] = value;

			// Position/type dependency
			if (
				field === "position" &&
				value === "LEFT" &&
				prev.type === "RIGHT_NAV"
			) {
				updated.type = "EXTERNAL";
			}

			return updated;
		});
	};

	const uploadFile = (file) => {
		try {
			// Prepare payload for your fluxAction API
			const formData = new FormData();
			formData.append("imageType", "rightNavIcon");
			formData.append("domainName", urlName); // replace with actual domain
			formData.append("image", file);
			// if (bg.imageId) {
			// 	formData.append("imageId", bg.imageId);
			// }
			UpdateImageAssetsAction.updateImageAssets(formData);
			// Call your API
		} catch (error) {
			message.error("Failed to upload image.");
		}

		return false; // prevents antd Upload default auto-upload behavior
	};

	const onUploadNewNav = () => {
		let data = { ...createNavData };
		if (!data.tabType || data.tabType.toUpperCase() !== 'CORE') {
			data["tabType"] = profile ? "PROFILE" : "DOMAIN";
		}
		if (profile) {
			data['profileId'] = getProfileId();
		}

		const errors = validateNavData(data, !!data.tabId);
		if (Object.keys(errors).length > 0) {
			message.error(Object.values(errors)[0]);
			return;
		}

		// Cache padding & background locally so they survive the re-fetch
		if (data.tabId && (data.type === 'RIGHT_NAV' || data.coreId === 'LIA' || data.coreId === 'AI_CHAT')) {
			savedNavStylesRef.current[data.tabId] = {
				padding: data.padding,
				background: data.background,
			};
		}

		if (data.tabId) {
			UpdateNavigationListAction.updateNavigation({ navigationTab: data });
		} else {
			CreateNavigationAction.createNavigation(data);
		}
	};

	const onEdit = (data) => {
		const updateData = {
			...INITIAL_NAV_DATA,
			...data,
			padding: data.padding !== undefined ? data.padding : 0,
			background: data.background || '',
		};
		setCreateNavData(updateData);
		setAddModal(!addModal);
	};
	const onDelete = (data) => {
		let fields = {};
		fields["tabId"] = data.tabId;
		fields["domainName"] = data.domainName;
		if (profile) {
			fields['profileId'] = getProfileId();
		}
		return Modal.confirm({
			title: "Are you sure?",
			content: "Would you like to permanently delete?",
			onOk: () => {
				DeleteNavigationItemAction.deleteNavigation(fields);
			},
			onCancel: () => {
				// console.log('Cancelled!');
			},
		});
	};
	const totalNavCount =
		topSection.length +
		middleSection.length +
		bottomSection.length +
		leftSection.length;

	const isAddDisabled = totalNavCount >= 15;
	return (
		<LCLocaleProvider>
			<div className="page-wrapper">
				<div className="border-class">
					<div className={styles.navWrapper}>
						<div className={styles.headSection}>
							<div className={styles.header}>
								<h2>Right & Left Custom Navigation</h2>
								{/* <p>{`Shape navigation around how your team works. Drag and drop to set link placement. Add new links and actions for total control.`}</p> */}
							</div>
							<div className={styles.buttonWrapper}>
								<span>
									<Button
										type="default"
										className="secondary-btn"
										disabled={isAddDisabled}
										onClick={() => onClickAddModal()}>
										+ Add New
									</Button>
								</span>
								<span>
									<Button
										type="primary"
										className="primary-btn"
										loading={updateLoader}
										onClick={() => saveNavData()}>
										Save Changes
									</Button>
								</span>
							</div></div>
						{loader ? (
							<div className={styles.loader}>
								<Spin></Spin>
							</div>
						) : (
							<div className={styles.tableWrapper}>
								{SECTIONS.map((section) => (
									<div key={section.id} className={styles.tableSection}>
										<h4 className={styles.tableHeader}>{section.label}</h4>
										<CustomTable
											columns={COLUMNS}
											data={getData(section.id)}
											tableId={section.id}
											onDragStart={onDragStart}
											onDrop={onDrop}
											onReorder={onReorder}
											showHeader={section.showHeader}
											onVisibilityChange={handleVisibilityChange}
											onEdit={onEdit}
											onDelete={onDelete}
											isProfile={profile}
										/>
									</div>
								))}
							</div>
						)}
					</div>
				</div>
				{addModal && (
					<div className={`addrole-settings ${styles.navModalWrapper}`}>
						<section className="card">
							<ModalForm
								width={1000}
								header={"Right Navigation Tab"}
								label={""}
								description={""}
								enableButton={false}
							// buttonLabel = {props.filterEdit ? 'Update Group' :'Create New Keyword Group'}
							>
								<NavModalFields
									uploadFile={uploadFile}
									data={createNavData}
									handleCancel={onClickAddModal}
									changeNavItems={changeNavItems}
									onUploadNewNav={onUploadNewNav}
								/>
							</ModalForm>
						</section>
					</div>
				)}
			</div>
		</LCLocaleProvider>
	);
};
